import clsx from "clsx";
import {useRef, useEffect} from "react";
import {useHistory, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import {Formik} from "formik";
import {useDropzone} from "react-dropzone";
import {AddFileLabFormSchemaValidation} from "config/form/FormValidation/AdminLaboratorium";
import {DashboardLayout} from "components/Layout";
import {SubmitButton} from "components/Button";
import {Select} from "components/Form";
import {convertErrorMessageFormat} from "utils/converter";
import {getTodayDate} from "utils/date";
import {useToast} from "hooks/useToast";
import {useAddFileLab} from "hooks/AdminLaboratorium/FileLab";
import {useUploadFile} from "hooks/Misc";
import {useDetailPendaftaranPasien} from "hooks/PendaftaranPasien";

const FormInitialValues = {
	fileBuffer: undefined,
	file: "",
	visibility: "",
	idPatient: "",
	idHospital: "",
	date: getTodayDate("YYYY-MM-DD"),
};

const visibilityOptions = [
	{
		value: "visible",
		label: "Live",
	},
	{
		value: "hidden",
		label: "Staff Only",
	},
];

export function PendaftaranPasienFileAddPage() {
	const {idPatient} = useParams();
	const {data: detailPendaftaranPasien} = useDetailPendaftaranPasien(idPatient);
	const {getRootProps, getInputProps} = useDropzone({
		accept: {
			"application/pdf": [],
		},
		onDropAccepted: files => {
			formikRef.current.setFieldValue("fileBuffer", files);
		},
		onDropRejected: res => {
			formikRef.current.setFieldError("fileBuffer", "Hanya menerima format .PDF");
		},
	});

	const selectedHospital = useSelector(state => state.HospitalsReducer.selectedHospital);

	const {showToast} = useToast();
	const formikRef = useRef();
	const history = useHistory();

	const {mutate: createDataFileLab, isLoading} = useAddFileLab();
	const {mutateAsync: uploadFile, isLoading: isUploading} = useUploadFile();

	const uploadAllFiles = async files => {
		const promises = await Promise.all(
			files.map(file => {
				let formData = new FormData();

				formData.append("file", file);

				return uploadFile(formData);
			}),
		);

		return promises.map((promise, idx) => ({
			name: files[idx]?.name ?? "",
			url: promise.data.file ?? "",
		}));
	};

	const handleSubmitForm = async formValue => {
		const {fileBuffer, ...formDataValue} = formValue;
		let files = await uploadAllFiles(fileBuffer);

		createDataFileLab(
			{
				...formDataValue,
				idHospital: selectedHospital.Hospital.id,
				name: fileBuffer.name,
				files,
			},
			{
				onSuccess: () => {
					showToast("success", "Data file labs berhasil ditambahkan", 3000);
					window.scrollTo(0, 0);
					history.push(`/pendaftaran-pasien/show/${idPatient}?tabIndex=2`);
				},
				onError: res => {
					showToast("error", convertErrorMessageFormat(res.response.status, res.response.data.message), null);
				},
			},
		);
	};

	const handleUploadFile = e => {
		const file = e.target.files[0];

		formikRef.current.setFieldValue("fileBuffer", file);
	};

	useEffect(() => {
		formikRef.current?.setFieldValue("idPatient", detailPendaftaranPasien?.data?.patient?.id);
	}, [detailPendaftaranPasien]);

	return (
		<DashboardLayout>
			<Formik
				initialValues={FormInitialValues}
				innerRef={formikRef}
				validationSchema={AddFileLabFormSchemaValidation}
				onSubmit={handleSubmitForm}>
				{({handleSubmit, handleBlur, handleChange, values, errors, touched, setFieldValue, setTouched}) => {
					return (
						<div class="box-white setting-content h-100 no-border p24">
							<div class="d-flex justify-content-between flex-wrap align-items-center mb-4">
								<div class="ttl-20">Upload file baru</div>
							</div>

							<hr />

							<div class="text-bold mb-4">
								<a
									class="text-dark"
									style={{cursor: "pointer"}}
									onClick={() => history.push(`/pendaftaran-pasien/show/${idPatient}`)}>
									<span class="icon-ico-back me-2" />
								</a>
								Detail Pasien : {detailPendaftaranPasien?.data?.fullName}
							</div>

							<div class="row gx-2 mb-5">
								<div class="col-sm-6 col-6">
									<Select
										required
										label="Visibility"
										name="visibility"
										options={visibilityOptions}
										placeholder="Pilih jenis visibility..."
										onChange={selected => setFieldValue("visibility", selected.value)}
									/>
									<div class="setting-item">
										<label class="setting-label required">Rumah Sakit</label>
										<input
											disabled
											class={clsx("form-control")}
											name="idHospital"
											placeholder=""
											type="text"
											value={selectedHospital.Hospital.name}
										/>
									</div>

									<div class="setting-item">
										<label class="setting-label required">Pasien</label>
										<input
											disabled
											class={clsx("form-control")}
											name="idPatient"
											placeholder=""
											type="text"
											value={detailPendaftaranPasien?.data?.fullName}
										/>
									</div>
								</div>

								<div class="col-sm-6 col-6" />
								<div class="col-sm-12 col-12">
									<div
										class="item-wrapper one "
										{...getRootProps({className: "dropzone"})}
										style={{cursor: "pointer"}}>
										<div class="item">
											<div class="item-inner">
												<div class="item-content">
													<div class="image-upload">
														<label for="file_upload" style={{cursor: "pointer"}}>
															<img alt="" class="uploaded-image" src="" />
															<div class="h-100">
																<div class="dplay-tbl">
																	<div class="dplay-tbl-cell">
																		{/* <i class="fa fa-cloud-upload"></i> */}
																		<span className="icon-upload-cloud icon-large" />

																		<h5>
																			{values.fileBuffer ? (
																				<>
																					<ul>
																						{values.fileBuffer?.map(
																							(file, idx) => (
																								<li
																									key={idx}
																									className="text-start">
																									{file.name}
																								</li>
																							),
																						)}
																					</ul>
																				</>
																			) : (
																				<b>Pilih file yang akan di upload</b>
																			)}
																		</h5>
																		{!values.fileBuffer && (
																			<h6 class="mt-10 mb-70 ft-14">
																				Hanya menerima format pdf
																			</h6>
																		)}
																	</div>
																</div>
															</div>
															<input
																accept="application/pdf"
																class="image-input"
																name="fileBuffer"
																type="file"
																value=""
																onChange={handleUploadFile}
																{...getInputProps()}
															/>
														</label>
													</div>
													{errors?.fileBuffer && (
														<p className="form-error-item-message">{errors?.fileBuffer}</p>
													)}
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>

							<div class="text-center my-3">
								<SubmitButton
									className="btn btn-orange d-inline-block mw-250"
									disabled={!AddFileLabFormSchemaValidation.isValidSync(values)}
									loading={isLoading || isUploading}
									text="Save"
									onClick={handleSubmit}
								/>
							</div>
						</div>
					);
				}}
			</Formik>
		</DashboardLayout>
	);
}
